// // auth/hooks/useLogout.ts

// import { useMutation, useQueryClient } from "@tanstack/react-query";
// import { useNavigate } from "react-router-dom";
// import { useAppDispatch } from "@/store/hooks";
// import { authApi } from "../api/auth.api";
// import { clearAuthState } from "../utils/auth.utils";
// import type { AuthCallbacks } from "../types/auth.types";
// import { logger } from "@/core";
// import { toastService } from "@/core/services/toast.service";

// /**
//  * useLogout — Handles user logout
//  *
//  * Flow:
//  * 1. Call logout API (clears httpOnly cookie on server)
//  * 2. Clear Redux state
//  * 3. Clear React Query cache
//  * 4. Redirect to login
//  */
// export const useLogout = (callbacks?: AuthCallbacks) => {
//   const dispatch = useAppDispatch();
//   const navigate = useNavigate();
//   const queryClient = useQueryClient();

//   return useMutation<void, Error, void>({
//     mutationFn: () => authApi.logout(),

//     onSuccess: () => {
//       toastService.success("Logout", "logout successfully");
//       clearAuthState(dispatch, queryClient);
//       navigate("/login", { replace: true });
//       logger.info("Logout successful");
//       callbacks?.onSuccess?.();
//     },

//     onError: (error) => {
//       toastService.error("Logout Failed", error.message);
//       // Even on error, clear local state (server session might be expired)
//       clearAuthState(dispatch, queryClient);
//       navigate("login", { replace: true });
//       logger.error("Logout API failed, cleared local state anyway", error);
//       callbacks?.onError?.(error);
//     },
//   });
// };

// ============================================================================
// DESTINATION:  src/features/auth/hooks/useLogout.ts
// ACTION:       REPLACE your existing file
// ============================================================================

/**
 * ============================================================================
 * FILE: src/features/auth/hooks/useLogout.ts
 * ============================================================================
 * Logout that actually ends the session everywhere: the server bumps the version
 * (killing every device), this clears local Redux + React Query, tells the other
 * tabs, and navigates.
 *
 * No SessionContext — the teardown is inline here, because that is the only place
 * it is needed.
 *
 * ============================================================================
 * THE ORDER MATTERS, AND SO DOES THE catch
 * ============================================================================
 * We call the API, but we clear locally REGARDLESS of whether it succeeds. A user
 * who pressed Logout must end up logged out even if the network is down — they may
 * be handing the laptop to someone. So the API failure is swallowed on purpose;
 * the local teardown always runs.
 */

import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { authApi } from "../api/auth.api";
import { useAppDispatch } from "@/store/hooks";
import { clearUser } from "@/store/slices/auth";
import { resetPaymentSourceType } from "@/store/slices/paymentSourceType/paymentSourceTypeSlice";
import { resetInvoiceSession } from "@/store/slices/invoiceSessionSlice/invoiceSessionSlice";
import { broadcastLoggedOut } from "@/core/utils/session-broadcast";
import { toastService } from "@/core/services/toast.service";
import { logger } from "@/core";

interface AuthCallbacks {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useLogout = (callbacks?: AuthCallbacks) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /**
   * The local teardown. Everything that must be gone after logout, in one place.
   *
   * ⚠️ RESET THE PAYMENT SLICES TOO. If you only clearUser(), the next person on
   * this browser inherits the previous user's invoice and discount context — a
   * $15 referral QR belonging to someone else. clearUser alone is not logout.
   */
  const teardown = () => {
    dispatch(clearUser());
    dispatch(resetPaymentSourceType());
    dispatch(resetInvoiceSession());
    // If you have onchainDeposit / myStripePayment reset actions, dispatch them
    // here too — any slice that holds per-session payment state must be cleared.

    // Drop cached queries so a re-login does not read a previous user's cache.
    queryClient.removeQueries();
  };

  return useMutation<void, Error, void>({
    mutationFn: async () => {
      try {
        // Server bumps sessionVersion -> every device's session dies, not just
        // this browser's.
        await authApi.logout();
      } catch (error) {
        // Swallowed on purpose — see the file header. The user pressed Logout;
        // they get logged out locally no matter what the server said.
        logger.warn(
          "Logout API failed; clearing locally anyway",
          error as Error,
        );
      }
    },

    onSuccess: () => {
      // Runs after mutationFn resolves (which it always does — the catch above
      // means it never rejects from an API failure).
      teardown();
      broadcastLoggedOut(); // every other tab in this browser
      toastService.success("Logout", "Logged out successfully");
      navigate("/login", { replace: true });
      callbacks?.onSuccess?.();
    },

    onError: (error) => {
      // Only reachable if teardown itself throws — genuinely unexpected. Still
      // get the user to /login.
      logger.error("Unexpected logout error", error);
      teardown();
      navigate("/login", { replace: true });
      callbacks?.onError?.(error);
    },
  });
};
